/* ============================================================================
   PDP v2 — the configurator. One module, no framework.

   The whole point: every state change is LOCAL. No fetch, no section re-render,
   no ?section_id= round trip — nothing touches the network until submit. All
   variant data arrives once, in the JSON tag the section prints. On submit there
   are two calls: /cart/add.js, then /cart.js to refresh the header count, since
   this theme's cart type is `notification` and header-home renders its count
   server-side with no component to re-render.

   Pricing rules that are not negotiable (see docs/pdp-v2-reference.md §4c):
     1. The ladder headline is always LIST price. A ladder must never quote a
        price the visitor hasn't opted into.
     2. Each rung carries its conditional price as a second line, naming the
        condition — Price Marking Order (amended, in force 6 April 2026).
     3. Per-unit price on every tier, formatted identically.
     4. The resting dock "from" price is list-based.
     5. Delivery is stated before the CTA.
     6. Tier selection survives a flavour or size change (Baymard #2712).

   Families: loose packs get the subscription, duration tiers get the member
   price, never both and never the other's. What those two are CALLED is a
   setting — "Packs" and "Bundles" by default — while the pdp.family metafield
   values stay `pack` and `daypack`. Renaming data to match copy is how a
   contract rots.
   ============================================================================ */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Tell the rest of the page a sticky dock owns the bottom of the viewport, so
     floating widgets (the chat cluster) can sit above it instead of on top of
     the Add-to-bag button. A class on <html>, not padding on <body> — writing
     global layout from inside a section is what the old sticky bar got wrong. */
  if (document.querySelector('[data-pdp2-dockbar]')) {
    document.documentElement.classList.add('pdp2-has-dock');
  }

  document.querySelectorAll('[data-pdp2-dock-root]').forEach(function (root) {
    var cfgEl = root.querySelector('[data-pdp2-config]');
    if (!cfgEl) return;
    var cfg = null;
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      /* The config is generated Liquid, so the realistic failure is a stray
         comma. A dead Choose-your-pack button is the worst outcome on the page,
         so retry once with trailing commas stripped — and keep shouting, because
         this is still a bug that wants fixing at the source. */
      console.error('pdp2: config did not parse, retrying tolerantly —', e);
      try {
        cfg = JSON.parse(cfgEl.textContent.replace(/,\s*([}\]])/g, '$1'));
        console.warn('pdp2: config parsed only after stripping trailing commas. Fix the section.');
      } catch (e2) {
        console.error('pdp2: config unrecoverable, dock stays inert', e2);
        return;
      }
    }
    try {
      init(root, cfg);
    } catch (e) {
      console.error('pdp2: init failed', e);
    }
  });

  /* ------------------------------------------------------- member unlock
     A QR code on a flyer points at Shopify's own discount link:

       /discount/SAVE20?redirect=/products/carnivore-crisps%3Fmember%3DSAVE20

     Shopify applies the code to the session, then lands the visitor on the
     product page with ?member=SAVE20. That param sets a sessionStorage flag, so
     member pricing shows for the rest of the visit and not only on the page they
     landed on.

     The parameter on its own proves nothing — anyone can type it. So when a
     member code IS configured, the parameter only prompts a check: the real
     answer comes from /cart.js, which reports the codes applied to the session
     and whether each is applicable, and does so even when the cart is EMPTY:

       { "item_count": 0,
         "discount_codes": [ { "code": "FREESHIPNEW", "applicable": true } ] }

     That is a genuine verification of the thing being claimed, so it is what
     unlocks the price. With no code configured there is nothing to verify
     against and the parameter alone unlocks, which stays safe for the reason
     below but is the weaker mode.

     Either way the member price never changes the total: it renders as a second
     labelled figure saying "applied at checkout with the code SAVE20", and the
     CTA charges list price. If a discount ever needs to drive the TOTAL it has
     to come from the cart, verified like this — not from a URL. */
  var MEMBER_FLAG = 'pp_member_session';

  function memberUnlock(paramName) {
    var name = paramName || 'member';
    var found = null;
    try {
      var v = new URLSearchParams(window.location.search).get(name);
      if (v !== null && v !== '' && v !== '0' && v.toLowerCase() !== 'false') found = v;
    } catch (e) {
      /* no URLSearchParams: no unlock, which is the safe direction */
    }
    try {
      if (found) sessionStorage.setItem(MEMBER_FLAG, found);
      else found = sessionStorage.getItem(MEMBER_FLAG);
    } catch (e) {
      /* private mode: the unlock lasts for this page only */
    }
    return found;
  }

  function init(root, C) {
    /* ---------------------------------------------------------------- data */
    /* A code arriving in the URL names itself, so the figure shown and the code
       the customer types cannot disagree. Whether it also UNLOCKS depends on
       whether there is a configured code to verify against. */
    var unlocked = memberUnlock(C.memberUnlockParam);
    if (unlocked && unlocked !== '1' && unlocked.toLowerCase() !== 'true') {
      C.memberCode = unlocked.toUpperCase();
    }
    var expectedCode = String(C.memberCode || '').trim();
    if (unlocked && !expectedCode) {
      /* Nothing to verify against — the parameter is all there is. */
      C.showMember = true;
    }
    var V = (C.variants || []).filter(function (v) {
      return v.count > 0;
    });
    if (!V.length) return;

    var CUR = C.currency || '£';
    var UNIT = C.unit || 'bag';
    var UNITS = C.unitPlural || UNIT + 's';

    /* The flavour axis is discovered, not configured and never parsed out of a
       title: group variants by (size, count, days) and the option position that
       separates the members of a group IS the flavour. Works whether flavour is
       option1 (crisps) or option2 (bars). */
    var flavIdx = detectFlavourIdx();

    var sizes = distinct(
      V.map(function (v) {
        return v.size;
      })
    ).filter(Boolean);
    /* Never ship a one-option control: step 1 hides itself at one size group,
       the same rule the flavour row follows at one flavour. */
    var useSize = sizes.length > 1;

    var flavours = flavIdx < 0 ? [] : distinct(V.map(pickFlav));
    var useFlav = flavours.length > 1;

    var S = {
      size: useSize ? sizes[0] : null,
      flavour: useFlav ? flavours[0] : flavIdx < 0 ? null : flavours[0],
      tierId: null,
      addons: {}, // id -> true, so several can be added at once
      plan: 'once', // one-off is always the pre-selected plan
      touched: false
    };

    /* Set after a successful add, so the button stops saying "Add to bag" for a bag
       that is no longer empty. Per page load, which is the life of the dock. */
    var addedOnce = false;

    /* ------------------------------------------------------------- elements */
    var el = {
      sheet: root.querySelector('[data-pdp2-sheet]'),
      body: root.querySelector('[data-pdp2-body]'),
      grab: root.querySelector('[data-pdp2-grab]'),
      open: root.querySelector('[data-pdp2-open]'),
      close: root.querySelector('[data-pdp2-close]'),
      dockL1: root.querySelector('[data-pdp2-dock-l1]'),
      dockCta: root.querySelector('[data-pdp2-dock-cta]'),
      strip: root.querySelector('[data-pdp2-strip]'),
      readback: root.querySelector('[data-pdp2-readback]'),
      total: root.querySelector('[data-pdp2-total]'),
      totalUnit: root.querySelector('[data-pdp2-total-unit]'),
      add: root.querySelector('[data-pdp2-add]'),
      addLabel: root.querySelector('[data-pdp2-add-label]'),
      addAmt: root.querySelector('[data-pdp2-add-amt]'),
      micro: root.querySelector('[data-pdp2-micro]')
    };
    if (!el.sheet || !el.body) return;

    var lastTotal = null;

    /* -------------------------------------------------------------- helpers */
    function distinct(arr) {
      var out = [];
      arr.forEach(function (x) {
        if (x !== null && x !== undefined && out.indexOf(x) === -1) out.push(x);
      });
      return out;
    }

    function pickFlav(v) {
      return flavIdx < 0 ? null : (v.options || [])[flavIdx];
    }

    function detectFlavourIdx() {
      var groups = {};
      V.forEach(function (v) {
        var k = (v.size || '') + '|' + v.count + '|' + v.days;
        (groups[k] = groups[k] || []).push(v);
      });
      var keys = Object.keys(groups);
      for (var g = 0; g < keys.length; g++) {
        var grp = groups[keys[g]];
        if (grp.length < 2) continue;
        var n = (grp[0].options || []).length;
        for (var i = 0; i < n; i++) {
          var vals = distinct(
            grp.map(function (v) {
              return (v.options || [])[i];
            })
          );
          if (vals.length === grp.length) return i;
        }
      }
      return -1;
    }

    /* £12 for round pounds, £12.34 otherwise — used for prices inside prose. */
    function m(cents) {
      var n = (cents || 0) / 100;
      return CUR + (n % 1 === 0 ? String(n) : n.toFixed(2));
    }
    /* Always two decimals — used wherever a number changes in place, so the
       digit count is stable and tabular-nums can do its job. */
    function m2(cents) {
      return CUR + ((cents || 0) / 100).toFixed(2);
    }

    /* Sentence-start capital for a noun that arrives lowercase from a setting. */
    function cap(str) {
      str = String(str || '');
      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
      });
    }

    function buzz(ms) {
      /* Android only — the Vibration API has never shipped in iOS Safari, so
         the tactile illusion is carried visually and this is a bonus. */
      if (navigator.vibrate) {
        try {
          navigator.vibrate(ms);
        } catch (e) {
          /* no-op */
        }
      }
    }

    function label(key, fallback) {
      var v = (C.labels || {})[key];
      return v && String(v).trim() ? v : fallback;
    }

    function sizeMeta(key) {
      var found = (C.sizeLabels || []).filter(function (b) {
        return b.key === key;
      })[0];
      return found || { key: key, label: key, sub: '', note: '' };
    }

    /* "Add 1 more large bags" is wrong, and so is "for one travel size bags".
       The size label is the merchant's plural, so drop its trailing s. English
       only, and crude, but a visible grammar error is worse. One copy of the
       rule, because the hero needs the same singular and cannot reach in here —
       it travels on the change event instead. */
    function oneOf(word) {
      return /s$/.test(word) ? word.slice(0, -1) : word;
    }

    /* Visitor-facing family words, all from settings, so "Bundles" and
       "30-day bundle" can never drift apart. */
    function fam(key, fallback) {
      var v = (C.familyLabels || {})[key];
      return v && String(v).trim() ? String(v) : fallback;
    }
    var DAYPACK_LABEL = fam('daypack', 'Bundles');
    var DAYPACK_NOUN = fam('daypackNoun', 'bundle');
    var DAYPACK_NOUNS = fam('daypackNounPlural', DAYPACK_NOUN + 's');

    function flavMeta(key) {
      var found = (C.flavourLabels || []).filter(function (b) {
        return b.key === key;
      })[0];
      return found || { key: key, desc: '', image: null };
    }

    /* ------------------------------------------------------- the ladder view */
    /* Where a tier with no pdp.size_group goes. Excluding it from every path
       loses it entirely (a new variant silently missing from the sheet, and the
       nudge stepping over it). Showing it in every path is worse in a different
       way: step 1 stops filtering, because both sizes list the same rung. So it
       goes in exactly ONE path — the default group — which keeps the size step
       meaningful and keeps the tier reachable. The theme editor names any
       variant in this state so the metafield gets set properly. */
    var UNGROUPED_HOME = useSize
      ? (C.defaultSizeGroup && sizes.indexOf(C.defaultSizeGroup) > -1 ? C.defaultSizeGroup : sizes[0])
      : null;

    function sizeOf(v) {
      return v.size || UNGROUPED_HOME;
    }

    function ladder() {
      return V.filter(function (v) {
        if (useSize && sizeOf(v) !== S.size) return false;
        if (flavIdx >= 0 && S.flavour !== null && pickFlav(v) !== S.flavour) return false;
        return true;
      }).sort(function (a, b) {
        return a.count - b.count || a.price - b.price;
      });
    }

    function cur() {
      var L = ladder();
      var found = L.filter(function (v) {
        return v.id === S.tierId;
      })[0];
      return found || L[0] || null;
    }

    function tierIndex() {
      var L = ladder();
      var c = cur();
      for (var i = 0; i < L.length; i++) if (L[i].id === c.id) return i;
      return 0;
    }

    /* Baymard #2712: switching flavour or size must NOT reset you to the
       cheapest variant. Keep the same tier by (count, days), then by days,
       then by position — only fall back to the first rung if nothing matches. */
    function keepTier(prev) {
      var L = ladder();
      if (!L.length) {
        S.tierId = null;
        return;
      }
      if (!prev) {
        S.tierId = L[0].id;
        return;
      }
      var by = function (fn) {
        return L.filter(fn)[0];
      };
      var hit =
        by(function (v) {
          return v.count === prev.count && v.days === prev.days;
        }) ||
        by(function (v) {
          return prev.days > 0 && v.days === prev.days;
        }) ||
        L[Math.min(prev._idx == null ? 0 : prev._idx, L.length - 1)] ||
        L[0];
      S.tierId = hit.id;
    }

    /* ------------------------------------------------------------- families */
    function canSub() {
      var c = cur();
      return !!(C.subEnabled && c && c.family === 'pack' && c.planId && c.planPrice != null);
    }

    function canMember() {
      var c = cur();
      return !!(C.showMember && c && c.family === 'daypack');
    }

    function memberPrice() {
      var c = cur();
      if (!c) return 0;
      return Math.round((c.price * (100 - (C.memberPercent || 20))) / 100);
    }

    /* -------------------------------------------------------------- add-ons
       An explicit list of variants the merchant chose, in their order. No
       matching logic and no derived prices: each row costs exactly what the cart
       will charge for it, which is the only way the sheet total and the cart can
       agree. Any number can be ticked. */
    function addonList() {
      /* The selected tier's own add-ons win, so a 30-bag bundle can offer
         something a single bag shouldn't. The product-level list is the fallback
         for tiers that don't name their own. */
      var c = cur();
      var own = c && c.addons && c.addons.length ? c.addons : null;
      return (own || C.addons || []).filter(function (a) {
        return a && a.id;
      });
    }

    function addonById(id) {
      return addonList().filter(function (a) {
        return String(a.id) === String(id);
      })[0];
    }

    function selectedAddons() {
      return addonList().filter(function (a) {
        return a.available && S.addons[a.id];
      });
    }

    function addonsCost() {
      return selectedAddons().reduce(function (sum, a) {
        return sum + a.price;
      }, 0);
    }

    /* --------------------------------------------------------------- pricing */
    function basePaid() {
      var c = cur();
      if (!c) return 0;
      /* Subscription is a real selling plan, so its price is exact.
         Member price is a code entered at checkout, so it does NOT change what
         is charged now — showing it as the total would quote a figure the cart
         won't match. */
      return S.plan === 'sub' && canSub() ? c.planPrice : c.price;
    }

    function total() {
      return basePaid() + addonsCost();
    }

    function perUnitPaid() {
      var c = cur();
      return c && c.count ? Math.round(basePaid() / c.count) : 0;
    }

    function per100(v, price) {
      if (!v || !v.grams || !v.count) return null;
      return Math.round((price * 100) / (v.count * v.grams));
    }

    function per100Blend() {
      var c = cur();
      if (!c || !c.grams) return null;
      var g = c.count * c.grams;
      var known = true;
      selectedAddons().forEach(function (a) {
        /* One add-on of unknown weight makes the blended figure a guess, so it
           is dropped rather than quietly wrong. */
        if (!a.grams || !a.count) known = false;
        else g += a.count * a.grams;
      });
      if (!known) return null;
      return g ? Math.round((total() * 100) / g) : null;
    }

    /* The real marginal rate to the next rung. Arithmetic anyone can check —
       not a decoy tier priced to flatter its neighbour. */
    /* The rungs of the current tier's own family, in ladder order. The nudge
       works within a family and not across families: Packs and Bundles are
       presented as separate groups carrying different conditional prices, so
       "add 10 more" pointing from a pack into a bundle sends you to another part
       of the sheet and quotes a rate against a different offer. */
    function familyLadder() {
      var c = cur();
      if (!c) return [];
      var fam = c.family || 'pack';
      return ladder().filter(function (v) {
        return (v.family || 'pack') === fam;
      });
    }

    function step() {
      var L = familyLadder();
      var c = cur();
      var i = -1;
      for (var k = 0; k < L.length; k++) {
        if (c && L[k].id === c.id) { i = k; break; }
      }
      if (i < 0) return null;
      var a = L[i];
      var b = L[i + 1];
      if (!a || !b) return null;
      var dn = b.count - a.count;
      var dp = b.price - a.price;
      if (dn <= 0 || dp <= 0) return null;
      return {
        next: b,
        dn: dn,
        dp: dp,
        rate: Math.round(dp / dn),
        from: Math.round(a.price / a.count)
      };
    }

    /* -------------------------------------------------------- unit-price line */
    function unitLine(v, price) {
      var parts = [];
      if (v.count > 1) parts.push(m2(Math.round(price / v.count)) + ' a ' + UNIT);
      var p100 = per100(v, price);
      if (p100) parts.push(m(p100) + '/100g');
      return parts.join(' · ');
    }

    /* ============================================================ RENDERING */

    function stepLabel(n, text, soft) {
      return (
        '<div class="pdp2-step"><i>' +
        n +
        '</i> ' +
        esc(text) +
        (soft ? ' <span class="pdp2-step__soft">' + esc(soft) + '</span>' : '') +
        '</div>'
      );
    }

    function sizeBlock(n) {
      if (!useSize) return '';
      var meta = sizeMeta(S.size);
      /* Glyphs are scaled from the real gram weight, so the size difference is
         visible rather than described. */
      var grams = {};
      V.forEach(function (v) {
        if (v.size && v.grams) grams[v.size] = Math.max(grams[v.size] || 0, v.grams);
      });
      var maxG = Math.max.apply(
        null,
        Object.keys(grams).map(function (k) {
          return grams[k];
        })
      );
      var html =
        stepLabel(n, label('size', 'Which bag?')) +
        '<div class="pdp2-sizes">' +
        sizes
          .map(function (sz) {
            var mt = sizeMeta(sz);
            var scale = maxG && grams[sz] ? Math.sqrt(grams[sz] / maxG) : 1;
            return (
              '<button type="button" class="pdp2-size" data-size="' +
              esc(sz) +
              '" aria-pressed="' +
              (sz === S.size) +
              '" style="--pdp2-glyph: ' +
              scale.toFixed(3) +
              ';">' +
              '<span class="pdp2-size__glyph"><i></i></span>' +
              '<span class="pdp2-size__txt"><b>' +
              esc(mt.label || sz) +
              '</b>' +
              (mt.sub ? '<em>' + esc(mt.sub) + '</em>' : '') +
              '</span></button>'
            );
          })
          .join('') +
        '</div>';
      /* An empty note renders nothing at all — no stray empty paragraph taking
         up the space the copy used to. */
      if (meta.note && String(meta.note).trim()) {
        html += '<p class="pdp2-micro">' + esc(meta.note) + '</p>';
      }
      return html;
    }

    function flavourBlock(n) {
      if (!useFlav) return '';
      return (
        stepLabel(n, label('flavour', 'Pick your flavour')) +
        '<div class="pdp2-flavours">' +
        flavours
          .map(function (f) {
            var mt = flavMeta(f);
            /* Block image, then this flavour's own variant image, then the
               product shot. The image area is rendered either way so two cards
               are never different heights. */
            var img =
              mt.image ||
              (V.filter(function (v) {
                return pickFlav(v) === f && v.image;
              })[0] || {}).image ||
              C.productImage;
            return (
              '<button type="button" class="pdp2-flavour" data-flav="' +
              esc(f) +
              '" aria-pressed="' +
              (f === S.flavour) +
              '">' +
              (img
                ? '<span class="pdp2-flavour__im"><img src="' +
                  esc(img) +
                  '" alt="" loading="lazy" width="500" height="344"></span>'
                : '<span class="pdp2-flavour__im pdp2-flavour__im--empty"></span>') +
              (mt.badge ? '<span class="pdp2-flavour__badge">' + esc(mt.badge) + '</span>' : '') +
              '<span class="pdp2-flavour__tick" aria-hidden="true">&#10003;</span>' +
              '<span class="pdp2-flavour__nm">' +
              esc(f) +
              '</span>' +
              (mt.desc && String(mt.desc).trim()
                ? '<span class="pdp2-flavour__ds">' + esc(mt.desc) + '</span>'
                : '') +
              '</button>'
            );
          })
          .join('') +
        '</div>'
      );
    }

    function rungHTML(v, selected) {
      var sub = C.subEnabled && v.family === 'pack' && v.planId && v.planPrice != null;
      var mem = C.showMember && v.family === 'daypack';
      var condPrice = sub ? v.planPrice : Math.round((v.price * (100 - (C.memberPercent || 20))) / 100);
      var flag = '';
      /* Only rendered when the underlying fact is true — no "Most people"
         unless it is, no glow on the expensive option. */
      if (v.best) flag = '<span class="pdp2-rung__flag pdp2-rung__flag--soft">Best per ' + esc(UNIT) + '</span>';

      var html =
        '<button type="button" class="pdp2-rung" data-tier="' +
        v.id +
        '" aria-pressed="' +
        !!selected +
        '">' +
        flag +
        (v.kicker ? '<span class="pdp2-rung__kick">' + esc(v.kicker) + '</span>' : '') +
        '<span class="pdp2-rung__nm">' +
        esc(v.name) +
        '</span>' +
        (v.line ? '<span class="pdp2-rung__ln">' + esc(v.line) + '</span>' : '') +
        '<span class="pdp2-rung__rt">' +
        '<span class="pdp2-rung__tot">' +
        m(v.price) +
        '</span>';

      if (v.count > 1) {
        html +=
          '<span class="pdp2-rung__per pdp2-rung__per--unit">' +
          m2(Math.round(v.price / v.count)) +
          ' a ' +
          esc(UNIT) +
          '</span>';
      }
      var p100 = per100(v, v.price);
      if (p100) html += '<span class="pdp2-rung__per">' + m(p100) + '/100g</span>';
      html += '</span>';

      /* The conditional price: both figures visible, each labelled with how you
         get it. Never applied to the headline. */
      if (sub || mem) {
        html +=
          '<span class="pdp2-rung__cond ' +
          (sub ? 'pdp2-rung__cond--sub' : 'pdp2-rung__cond--mem') +
          '"><i aria-hidden="true">' +
          (sub ? '&#8635;' : '&#9733;') +
          '</i> ' +
          m(condPrice) +
          ' ' +
          (sub ? 'on subscription' : 'member price') +
          (v.count > 1 ? ' <b>' + m2(Math.round(condPrice / v.count)) + ' a ' + esc(UNIT) + '</b>' : '') +
          '</span>';
      }

      return html + '</button>';
    }

    function familyGroup(fam, title, caption) {
      var rows = ladder().filter(function (v) {
        return v.family === fam;
      });
      if (!rows.length) return '';
      var c = cur();
      return (
        '<div class="pdp2-family"><b>' +
        esc(title) +
        '</b><em>' +
        esc(caption) +
        '</em></div><div class="pdp2-ladder">' +
        rows
          .map(function (v) {
            return rungHTML(v, c && v.id === c.id);
          })
          .join('') +
        '</div>'
      );
    }

    function amountBlock(n) {
      var head = stepLabel(n, label('amount', 'How much do you want?'));
      var L = ladder();
      var c = cur();

      /* Group by whatever families are actually present, in a defined order.
         Previously this rendered exactly the 'pack' and 'daypack' groups, so a
         variant with any other pdp.family value — someone types "bundle" —
         belonged to neither and vanished from the sheet entirely. A tier must
         never be able to disappear because of a typo in a metafield. */
      var order = [];
      L.forEach(function (v) {
        var f = v.family || 'pack';
        if (order.indexOf(f) === -1) order.push(f);
      });
      order.sort(function (a, b) {
        var rank = function (f) { return f === 'pack' ? 0 : f === 'daypack' ? 1 : 2; };
        return rank(a) - rank(b);
      });

      /* One family only: the headings are noise, so drop them. */
      if (order.length < 2) {
        return (
          head +
          '<div class="pdp2-ladder">' +
          L.map(function (v) {
            return rungHTML(v, c && v.id === c.id);
          }).join('') +
          '</div>'
        );
      }

      return head + order.map(function (f) {
        if (f === 'pack') {
          return familyGroup('pack', fam('pack', 'Packs'),
            fam('packCaption', C.subEnabled ? 'Buy once, or subscribe.' : ''));
        }
        if (f === 'daypack') {
          return familyGroup('daypack', DAYPACK_LABEL, fam('daypackCaption', 'Sized by how long they last.'));
        }
        /* An unrecognised family still gets its own group, titled with the value
           itself so it is obvious where it came from. */
        return familyGroup(f, f, '');
      }).join('');
    }

    function addonBlock(n) {
      var list = addonList();
      if (!list.length) {
        /* Battle-pass logic: the capability stays legible before you reach it,
           rather than being silently dropped. */
        return (
          stepLabel(n, label('addon', 'Add-ons'), '— optional') +
          '<div class="pdp2-locked"><i aria-hidden="true"></i><span>' +
          'Nothing to add alongside this one yet.' +
          '</span></div>'
        );
      }

      var c = cur();
      var basePer100 = c ? per100(c, c.price) : null;

      var html = stepLabel(n, label('addon', 'Anything else?'), '— optional') +
        (C.addonBody ? '<p class="pdp2-micro">' + esc(C.addonBody) + '</p>' : '') +
        '<div class="pdp2-addons">';

      list.forEach(function (a) {
        var on = !!(a.available && S.addons[a.id]);
        var name = a.title && a.title !== 'Default Title'
          ? a.productTitle + ' · ' + a.title
          : a.productTitle || a.title;

        /* An upsell that states where it is worse is the version this brand can
           defend — so if the add-on is dearer per gram than the base, say so on
           the row rather than in a note nobody reads. */
        var per100Addon = a.grams && a.count ? Math.round((a.price * 100) / (a.count * a.grams)) : null;
        var worse = per100Addon && basePer100 && per100Addon > basePer100;

        html +=
          '<button type="button" class="pdp2-addon" data-addon="' + a.id + '"' +
          ' aria-pressed="' + on + '"' + (a.available ? '' : ' disabled') + '>' +
          '<span class="pdp2-addon__box" aria-hidden="true">' + (on ? '&#10003;' : '&#10010;') + '</span>' +
          (a.image
            ? '<span class="pdp2-addon__im"><img src="' + esc(a.image) + '" alt="" loading="lazy" width="200" height="200"></span>'
            : '') +
          '<span class="pdp2-addon__mid"><b>' + esc(name) + '</b>' +
          (a.count && a.grams
            ? '<em>' + a.count + ' × ' + a.grams + 'g</em>'
            : '') +
          (a.available
            ? (worse
                ? '<span class="pdp2-addon__rate">' + m(per100Addon) + '/100g against ' + m(basePer100) +
                  '/100g for the pack — portioning costs more.</span>'
                : (a.count > 1
                    ? '<span class="pdp2-addon__rate">' + m2(Math.round(a.price / a.count)) + ' each</span>'
                    : ''))
            : '<span class="pdp2-addon__rate">Sold out</span>') +
          '</span>' +
          '<span class="pdp2-addon__amt">' + (on ? '' : '+') + m(a.price) + '</span>' +
          '</button>';
      });

      html += '</div>';
      return html;
    }

    /* The "might as well" nudge, as its own step.

       Worth flagging: the brief puts this in the sticky commit block, directly
       above the CTA, precisely so it is always visible at the decision point.
       Here it sits in the scrolling body instead, so it is only seen by someone
       who scrolls to it — a deliberate trade for not having a chip floating over
       the commit block. Easy to move back: render upsellBlock from renderCommit
       instead of renderSheet. */
    /* One substitution table for both nudge lines, so a placeholder can never
       work on one and silently not the other. */
    function fill(template, up, sizeWord) {
      return String(template)
        .replace(/\{count\}/g, up.dn)
        .replace(/\{units\}/g, up.dn === 1 ? UNIT : UNITS)
        .replace(/\{unit\}/g, UNIT)
        .replace(/\{size\}/g, sizeWord)
        .replace(/\{price\}/g, m(up.dp))
        .replace(/\{rate\}/g, m2(up.rate))
        .replace(/\{from\}/g, m2(up.from));
    }

    function upsellBlock(n) {
      if (!C.upsellEnabled) return '';
      var up = step();

      if (!up) {
        /* The ceiling line claims "this is the cheapest a bag we sell", so it
           only shows when that is true of the whole ladder — every family, not
           just the current one. Scoping it to a family would let a single-rung
           family claim to be the cheapest while a better rate sat next to it. */
        var c = cur();
        var L = ladder();
        var isCheapestPerUnit = !!c && c.count > 0 && L.length > 1 && L.every(function (v) {
          return !v.count || v.price / v.count >= c.price / c.count;
        });
        if (isCheapestPerUnit) {
          return (
            stepLabel(n, label('upsell', 'Might as well?')) +
            '<p class="pdp2-popcorn pdp2-popcorn--ceiling"><span><b>' +
            esc((C.upsellCeiling || 'This is the cheapest a {unit} we sell.').replace('{unit}', UNIT)) +
            '</b></span></p>'
          );
        }
        return '';
      }

      /* {size} names the format being recommended — "large bags", "travel bags" —
         from the size block's own label, so the nudge is explicit about what it
         is suggesting more of. With no size groups on the product it falls back
         to the unit noun, so the sentence still reads for the bars. */
      var sizeWord = up.dn === 1 ? UNIT : UNITS;
      if (useSize && S.size) {
        var sm = sizeMeta(S.size);
        sizeWord = String(sm.label || S.size).toLowerCase();
        if (up.dn === 1) sizeWord = oneOf(sizeWord);
      }
      var l1 = fill(C.upsellText || 'Add {count} more {size} for another {price}', up, sizeWord);
      var l2 = fill(C.upsellSubline || "That's {rate} a {unit} instead of {from}", up, sizeWord);

      return (
        stepLabel(n, label('upsell', 'Might as well?')) +
        '<button type="button" class="pdp2-popcorn" data-jump="' + up.next.id + '">' +
        '<span><b>' + esc(l1) + '</b><em>' + esc(l2) + '</em></span>' +
        /* A plus, not an arrow: this adds to what you already chose, it doesn't
           navigate anywhere. */
        '<span class="pdp2-popcorn__arw" aria-hidden="true">+</span></button>'
      );
    }

    function planBlock(n) {
      var c = cur();
      if (!c) return '';
      var sub = canSub();
      var mem = canMember();
      var perU = function (p) {
        return c.count > 1 ? m2(Math.round(p / c.count)) + ' a ' + UNIT : '';
      };

      var html =
        stepLabel(n, label('pay', 'How you pay')) +
        '<div class="pdp2-plans">' +
        '<button type="button" class="pdp2-plan" data-plan="once" aria-pressed="' +
        (S.plan === 'once') +
        '">' +
        '<span class="pdp2-plan__radio" aria-hidden="true"></span>' +
        '<span><span class="pdp2-plan__nm">Buy once</span>' +
        '<span class="pdp2-plan__ds">No commitment, no account.</span></span>' +
        '<span class="pdp2-plan__rt"><span class="pdp2-plan__t">' +
        m2(c.price) +
        '</span><span class="pdp2-plan__u">' +
        perU(c.price) +
        '</span></span></button>';

      if (sub) {
        var pct = Math.round(((c.price - c.planPrice) / c.price) * 100);
        html +=
          '<button type="button" class="pdp2-plan pdp2-plan--tall" data-plan="sub" aria-pressed="' +
          (S.plan === 'sub') +
          '">' +
          '<span class="pdp2-plan__radio" aria-hidden="true"></span>' +
          '<span><span class="pdp2-plan__nm">' +
          esc(C.subIntervalLabel || 'Subscribe monthly') +
          (pct > 0 ? ' <span class="pdp2-plan__tag">&minus;' + pct + '%</span>' : '') +
          '</span><span class="pdp2-plan__perks">' +
          (C.subPerks || [])
            .filter(Boolean)
            .map(function (p, i) {
              return '<em aria-hidden="true">' + ['&#8635;', '&#9889;', '&#10003;'][i] + '</em><span>' + esc(p) + '</span>';
            })
            .join('') +
          '</span></span>' +
          '<span class="pdp2-plan__rt"><span class="pdp2-plan__t">' +
          m2(c.planPrice) +
          '</span><span class="pdp2-plan__u">' +
          perU(c.planPrice) +
          '</span><span class="pdp2-plan__u">a month</span></span></button>';
      }

      if (mem) {
        var mp = memberPrice();
        html +=
          '<button type="button" class="pdp2-plan" data-plan="member" aria-pressed="' +
          (S.plan === 'member') +
          '">' +
          '<span class="pdp2-plan__radio" aria-hidden="true"></span>' +
          '<span><span class="pdp2-plan__nm">Member price <span class="pdp2-plan__tag">&minus;' +
          (C.memberPercent || 20) +
          '%</span></span>' +
          '<span class="pdp2-plan__ds">' +
          esc(C.memberNote || 'Returning customers — code on your flyer') +
          '</span></span>' +
          '<span class="pdp2-plan__rt"><span class="pdp2-plan__t">' +
          m2(mp) +
          '</span><span class="pdp2-plan__u">' +
          perU(mp) +
          '</span></span></button>';
      }
      html += '</div>';

      if (S.plan === 'member') {
        /* Say plainly that the figure arrives at checkout, so the sheet total
           and the cart total can't disagree. */
        html +=
          '<div class="pdp2-membernote">The ' +
          m(memberPrice()) +
          ' member price is applied at checkout with ' +
          (C.memberCode ? 'the code <code>' + esc(C.memberCode) + '</code>' : 'your flyer code') +
          '. Your bag shows ' +
          m(cur().price) +
          ' until then.</div>';
      }

      /* One 20% saving per order — the two conditional prices never stack. */
      if (sub && mem) {
        html += '<p class="pdp2-micro">One saving per order — the subscription and the member price don&rsquo;t stack.</p>';
      } else if (sub) {
        html += '<p class="pdp2-micro">' + cap(DAYPACK_NOUNS) + ' aren&rsquo;t subscriptions — they&rsquo;re bought outright, with a member price for returning customers.</p>';
      } else if (mem) {
        html += '<p class="pdp2-micro">' + cap(DAYPACK_NOUNS) + ' are bought outright. Want it arriving every month instead? ' + esc(fam('pack', 'Packs')) + ' subscribe.</p>';
      }
      return html;
    }

    /* Subscriptions ship free whatever the total, so a subscriber must not be
       counted up to a threshold they don't have to reach. Reads the same theme
       setting as the cart's shipping bar, so the sheet and the cart agree. */
    function subscriptionShipsFree() {
      return !!(C.subsShipFree !== false && S.plan === 'sub' && canSub());
    }

    function deliveryIsFree() {
      var threshold = C.freeShip || 0;
      return subscriptionShipsFree() || !threshold || total() >= threshold;
    }

    function shipBlock() {
      var p = total();
      var threshold = C.freeShip || 0;
      if (deliveryIsFree()) {
        return (
          '<div class="pdp2-ship"><b>Free UK delivery included.</b>' +
          (subscriptionShipsFree()
            ? ' Subscriptions always ship free, whatever the total — and priority.'
            : '') +
          '</div>'
        );
      }
      /* Starts at zero and reflects the real basket. No endowed progress. */
      var pct = Math.min(100, Math.round((p / threshold) * 100));
      return (
        '<div class="pdp2-ship"><b>' +
        m(threshold - p) +
        /* The threshold is already in the bold clause and on the meter, so
           repeating it as "below £100" said the same thing three times. */
        ' to free UK delivery</b> — delivery is ' + m(C.deliveryCharge || 500) + '.' +
        /* The other way out, where it is still useful to know. */
        (C.subsShipFree !== false && canSub()
          ? ' Or subscribe and it ships free, whatever the total.'
          : '') +
        '<span class="pdp2-meter"><i style="width: ' +
        pct +
        '%;"></i></span></div>'
      );
    }

    function renderSheet() {
      var keep = el.body.scrollTop;
      var n = 0;
      var html = '';
      if (useSize) html += sizeBlock(++n);
      if (useFlav) html += flavourBlock(++n);
      html += amountBlock(++n);
      html += addonBlock(++n);
      var upsell = upsellBlock(n + 1);
      if (upsell) {
        n += 1;
        html += upsell;
      }
      html += planBlock(++n);
      html += shipBlock();
      el.body.innerHTML = html;
      el.body.scrollTop = keep;

      /* 40ms stagger, capped at six rows — beyond that it reads as slow rather
         than choreographed. Dropped entirely under reduced motion. */
      if (!REDUCED.matches) {
        var rows = el.body.querySelectorAll('.pdp2-step, .pdp2-sizes, .pdp2-flavours, .pdp2-ladder, .pdp2-plans');
        Array.prototype.slice.call(rows, 0, 6).forEach(function (r, i) {
          r.style.animation = 'pdp2-rise 300ms var(--pdp2-e-decel) ' + i * 40 + 'ms both';
        });
      }

      el.body.querySelectorAll('[data-size]').forEach(function (b) {
        b.addEventListener('click', function () {
          setSize(b.getAttribute('data-size'));
        });
      });
      el.body.querySelectorAll('[data-flav]').forEach(function (b) {
        b.addEventListener('click', function () {
          setFlavour(b.getAttribute('data-flav'));
        });
      });
      el.body.querySelectorAll('[data-tier]').forEach(function (b) {
        b.addEventListener('click', function () {
          setTier(+b.getAttribute('data-tier'));
        });
      });
      el.body.querySelectorAll('[data-addon]').forEach(function (b) {
        b.addEventListener('click', function () {
          var id = b.getAttribute('data-addon');
          if (S.addons[id]) delete S.addons[id];
          else S.addons[id] = true;
          S.touched = true;
          buzz(S.addons[id] ? 12 : 8);
          render();
        });
      });
      var jump = el.body.querySelector('[data-jump]');
      if (jump) {
        jump.addEventListener('click', function () {
          setTier(+jump.getAttribute('data-jump'));
          var sel = el.body.querySelector('[data-tier][aria-pressed="true"]');
          if (sel) sel.scrollIntoView({ behavior: REDUCED.matches ? 'auto' : 'smooth', block: 'center' });
        });
      }

      el.body.querySelectorAll('[data-plan]').forEach(function (b) {
        b.addEventListener('click', function () {
          S.plan = b.getAttribute('data-plan');
          S.touched = true;
          buzz(10);
          render();
        });
      });
    }

    function renderDock() {
      var c = cur();
      if (!c) return;
      if (!S.touched) {
        /* Untouched dock keeps whatever the server rendered — the computed floor,
           or the manual override if one is set. Either way the JS leaves it. */
        if (el.dockCta) el.dockCta.textContent = C.dockCta || 'Choose your pack';
      } else {
        var t = total();
        if (el.dockL1) {
          /* Same shape the server rendered, struck price included, so picking a pack is
             a number changing rather than the line changing form. Subtracted, not
             scaled: the discount is a fixed amount, so quoting a percentage of the pack
             would disagree with checkout on every pack but one. */
          var off = C.offerAmountOff || 0;
          var head = off > 0
            ? '<s class="pdp2-dock__was">' + m2(t) + '</s><span>' + m2(Math.max(0, t - off)) + '</span>'
            : '<span>' + m2(t) + '</span>';
          el.dockL1.innerHTML =
            head + '<span>' + (c.count > 1 ? m2(perUnitPaid()) + ' a ' + esc(UNIT) : 'total') + '</span>';
        }
        /* Not "Add to bag": the button opens the sheet, and a label that
           promises to buy while opening a panel is a lie. */
        if (el.dockCta) el.dockCta.textContent = C.dockCtaChosen || 'Review & add';
      }

      if (el.strip) {
        var L = ladder();
        var html = '';
        if (useSize) {
          html +=
            '<button type="button" class="pdp2-mini pdp2-mini--size" data-size-swap aria-pressed="false">' +
            '<b>' +
            esc(sizeMeta(S.size).label || S.size) +
            '</b><small>tap to swap</small></button>';
        }
        html += L.map(function (v) {
          return (
            '<button type="button" class="pdp2-mini" data-mini="' +
            v.id +
            '" aria-pressed="' +
            (v.id === c.id) +
            '"><b>' +
            m(v.price) +
            '</b><small>' +
            esc(v.days ? v.days + '-day' : v.count + ' ' + (v.count === 1 ? UNIT : UNITS)) +
            '</small></button>'
          );
        }).join('');
        el.strip.innerHTML = html;
        el.strip.querySelectorAll('[data-mini]').forEach(function (b) {
          b.addEventListener('click', function () {
            setTier(+b.getAttribute('data-mini'));
          });
        });
        var swap = el.strip.querySelector('[data-size-swap]');
        if (swap) {
          swap.addEventListener('click', function () {
            var i = sizes.indexOf(S.size);
            setSize(sizes[(i + 1) % sizes.length]);
          });
        }
      }
    }

    function renderCommit() {
      var c = cur();
      if (!c) return;
      var t = total();

      if (el.readback) {
        var bits = [];
        if (useSize) bits.push(esc(sizeMeta(S.size).label || S.size));
        if (S.flavour) bits.push('<b>' + esc(S.flavour) + '</b>');
        bits.push(esc(c.name));
        selectedAddons().forEach(function (a) {
          bits.push('+ ' + esc(a.productTitle || a.title));
        });
        if (S.plan === 'sub') bits.push('Monthly');
        if (S.plan === 'member') bits.push('Member price at checkout');
        el.readback.innerHTML = bits.join(' · ');
      }

      roll(el.total, lastTotal, t);
      roll(el.addAmt, lastTotal, t);
      lastTotal = t;

      if (el.totalUnit) {
        var parts = [];
        if (c.count > 1) parts.push(m2(perUnitPaid()) + ' a ' + UNIT);
        var pb = per100Blend();
        if (pb) parts.push(m(pb) + '/100g');
        el.totalUnit.textContent = parts.join(' · ');
      }

      if (el.add) el.add.disabled = !c.available;
      if (el.addLabel) {
        if (c.available) {
          /* Once something of this product is in the bag, every later press is
             adding another, whichever variant is selected now. */
          el.addLabel.textContent = addedOnce ? 'Add another · ' : 'Add to bag · ';
        } else {
          el.addLabel.textContent = 'Sold out';
        }
      }

      /* Delivery is in the total before the CTA. No drip pricing. */
      if (el.micro) {
        var threshold = C.freeShip || 0;
        var tail =
          S.plan === 'sub'
            ? ' Skip or cancel any time after box two.'
            : S.plan === 'member'
              ? ' Member price applied at checkout with your code.'
              : '';
        var head = deliveryIsFree()
          ? (subscriptionShipsFree()
              ? 'Total includes free UK delivery — subscriptions always ship free.'
              : 'Total includes free UK delivery.')
          : 'Plus ' + m(C.deliveryCharge || 500) + ' UK delivery. Free over ' + m(threshold) + '.';
        el.micro.textContent = head + tail;
      }
    }

    /* The total rolls; the per-unit figure lands slightly before it, so cause
       appears to precede effect. Dropped entirely under reduced motion. */
    function roll(node, from, to) {
      if (!node) return;
      if (from == null || REDUCED.matches || from === to) {
        node.textContent = m2(to);
        return;
      }
      var t0 = performance.now();
      var ms = 480;
      (function frame(now) {
        var k = Math.min(1, (now - t0) / ms);
        var eased = 1 - Math.pow(1 - k, 3);
        node.textContent = m2(Math.round(from + (to - from) * eased));
        if (k < 1) requestAnimationFrame(frame);
      })(t0);
    }

    function render() {
      /* Switching family must reset an invalid plan rather than silently
         charging the wrong thing. */
      if (S.plan === 'sub' && !canSub()) S.plan = 'once';
      if (S.plan === 'member' && !canMember()) S.plan = 'once';
      /* Drop anything that is no longer on offer for the current tier, or has
         gone out of stock. Selections otherwise survive a tier change: with an
         explicit list there is no such thing as a mismatched add-on, so clearing
         them every time would be friction. But now that add-ons can be set per
         variant, moving to a tier that doesn't offer one must un-select it —
         otherwise the cart gets a line the sheet no longer shows. */
      Object.keys(S.addons).forEach(function (id) {
        var a = addonById(id);
        if (!a || !a.available) delete S.addons[id];
      });

      renderDock();
      if (el.sheet.open) renderSheet();
      renderCommit();
      announce();
    }

    function announce() {
      var c = cur();
      if (!c) return;
      /* The hero listens to this to keep its headline price identical to the
         dock's current selection. Formatting travels with it so there is one
         money formatter, not two. */
      var sizeLabelNow = useSize && S.size ? String(sizeMeta(S.size).label || S.size) : '';
      var detail = {
        variantId: c.id,
        variant: c,
        image: c.image,
        flavour: S.flavour,
        size: S.size,
        plan: S.plan,
        total: total(),
        listPrice: c.price,
        listPriceText: m(c.price),
        /* The new-customer price, formatted here so there is one money formatter on
           this page rather than a second one in the hero. Empty when no offer is on,
           which is the hero's signal to quote the list price plainly. */
        offerPriceText: C.offerAmountOff > 0 ? m(Math.max(0, c.price - C.offerAmountOff)) : '',
        count: c.count,
        grams: c.grams,
        sizeGroup: c.size || '',
        /* Both forms, because the hero writes "for 5 travel size bags" and
           "for one travel size bag" from the same label and must not own a
           second copy of the plural rule. Blank when the product has no size
           groups, which is the hero's signal to keep its own phrasing. */
        sizeLabel: sizeLabelNow,
        sizeLabelOne: sizeLabelNow ? oneOf(sizeLabelNow) : '',
        tierName: c.name,
        unitLine: unitLine(c, c.price),
        touched: S.touched
      };
      document.dispatchEvent(new CustomEvent('pdp2:change', { detail: detail }));
      /* Keep the old contract so anything already listening still works. */
      document.dispatchEvent(new CustomEvent('variant-card-picker:change', { detail: detail }));
    }

    /* ------------------------------------------------------------ mutations */
    function setSize(sz) {
      if (sz === S.size) return;
      var prev = cur();
      if (prev) prev._idx = tierIndex();
      S.size = sz;
      S.touched = true;
      buzz(12);
      keepTier(prev);
      render();
    }

    function setFlavour(f) {
      if (f === S.flavour) return;
      var prev = cur();
      if (prev) prev._idx = tierIndex();
      S.flavour = f;
      S.touched = true;
      buzz(8);
      keepTier(prev); // tier survives the flavour change — Baymard #2712
      render();
    }

    function setTier(id) {
      if (id === S.tierId) return;
      S.tierId = id;
      S.touched = true;
      buzz(12);
      render();
    }

    /* ------------------------------------------------------- sheet mechanics */
    /* Page lock. showModal() blocks clicks behind the sheet but NOT touch
       scrolling — on iOS a drag that starts on the header, the grab bar, the
       commit bar or the scrim scrolls the product page instead, and you come
       back to a sheet floating over somewhere else. `overflow: hidden` alone
       does not hold there, so the page is pinned with position:fixed and the
       offset put back on close. The theme's own .overflow-hidden class is the
       flimsy version of this, and this is the bug it does not fix. */
    var lockedY = 0;

    function lockPage() {
      lockedY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = -lockedY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }

    function unlockPage() {
      if (document.body.style.position !== 'fixed') return;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, lockedY);
    }

    function openSheet() {
      if (el.sheet.open) return;
      el.sheet.classList.remove('is-closing');
      if (typeof el.sheet.showModal === 'function') el.sheet.showModal();
      else el.sheet.setAttribute('open', '');
      lockPage();
      renderSheet();
      renderCommit();
    }

    function closeSheet() {
      if (!el.sheet.open) return;
      /* Exit is faster than enter — that's where the sense of weight comes
         from. Selections live in S, so they persist on reopen. */
      el.sheet.classList.add('is-closing');
      var done = function () {
        el.sheet.classList.remove('is-closing');
        el.sheet.close();
      };
      if (REDUCED.matches) done();
      else setTimeout(done, 250);
    }

    if (el.open) {
      el.open.addEventListener('click', function () {
        /* Always reopen. Committing straight from the dock meant a second tap
           bought whatever was configured with no chance to look at it again —
           and dismissing the sheet then left no way back to the choices. The
           sheet is where you buy; the dock is how you get to it. */
        openSheet();
      });
    }
    if (el.close) el.close.addEventListener('click', closeSheet);

    /* Every dismissal path ends in a close event — button, Escape, scrim tap,
       drag-to-dismiss, add-to-cart — so the unlock hangs off that one place
       rather than off each of them. */
    el.sheet.addEventListener('close', unlockPage);

    /* Escape: let <dialog> tell us, then run our own exit animation. */
    el.sheet.addEventListener('cancel', function (e) {
      e.preventDefault();
      closeSheet();
    });
    /* Scrim tap: a click whose target is the dialog itself landed on ::backdrop. */
    el.sheet.addEventListener('click', function (e) {
      if (e.target === el.sheet) closeSheet();
    });

    /* Drag to dismiss — M3's own thresholds: 56px positional or 125dp/s
       velocity, with rubber-banding above the top. */
    if (el.grab) {
      var y0 = null;
      var t0 = 0;
      var dy = 0;
      el.grab.addEventListener('pointerdown', function (e) {
        y0 = e.clientY;
        t0 = performance.now();
        dy = 0;
        el.sheet.style.transition = 'none';
        try {
          el.grab.setPointerCapture(e.pointerId);
        } catch (err) {
          /* no-op */
        }
      });
      el.grab.addEventListener('pointermove', function (e) {
        if (y0 === null) return;
        dy = e.clientY - y0;
        var v = dy > 0 ? dy : -Math.pow(-dy, 0.7);
        el.sheet.style.transform = 'translateY(' + v + 'px)';
      });
      var release = function () {
        if (y0 === null) return;
        var vel = dy / Math.max(1, performance.now() - t0);
        el.sheet.style.transition = '';
        el.sheet.style.transform = '';
        if (dy > 56 || vel > 0.125) closeSheet();
        y0 = null;
      };
      el.grab.addEventListener('pointerup', release);
      el.grab.addEventListener('pointercancel', release);
    }

    /* ------------------------------------------------------------ offer codes */
    /* The new-customer offer is two codes, and the customer should not have to type
       either. They are applied to the cart itself after the add, in ONE call, because
       /cart/update.js replaces the code set rather than adding to it — verified live:
       a cart holding POPUPCRISP handed FREESHIPNEW alone came back holding only
       FREESHIPNEW. So whatever is already applicable is re-sent alongside ours, and a
       code the customer arrived with survives.

       Not a cart permalink. /cart/<variant>:<qty>?discount=A,B does carry both codes and
       is the right shape for a LINK, but it replaces the bag instead of adding to it, so
       on a page it would quietly throw away whatever was already there — and it cannot
       carry a per-line selling plan, which this dock can. This route has neither limit.

       Shopify still decides who qualifies: an ineligible customer gets applicable false
       and is charged the normal price, exactly as at checkout. */
    function offerCodeList() {
      if (!C.offerCodes) return [];
      return C.offerCodes.split(',').map(function (code) {
        return code.trim();
      }).filter(Boolean);
    }

    function applyOfferCodes() {
      var ours = offerCodeList();
      if (!ours.length) return Promise.resolve();

      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (cart) {
          var seen = {};
          var codes = [];
          var add = function (code) {
            var key = String(code).toUpperCase();
            if (seen[key]) return;
            seen[key] = 1;
            codes.push(code);
          };
          ((cart && cart.discount_codes) || []).forEach(function (entry) {
            if (entry && entry.applicable === true && entry.code) add(entry.code);
          });
          ours.forEach(add);

          return fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: codes.join(',') })
          });
        })
        .catch(function (error) {
          /* A failed code must not cost the customer the add they asked for. */
          console.error('pdp2: offer codes', error);
        });
    }

    /* --------------------------------------------------------------- submit */
    function addToCart() {
      var c = cur();
      if (!c || !c.available) return;
      var btn = el.add;

      var items = [{ id: c.id, quantity: 1 }];
      if (S.plan === 'sub' && canSub()) items[0].selling_plan = c.planId;
      /* One line item per selected add-on, in the same call, so the cart total
         matches the sheet to the penny. */
      selectedAddons().forEach(function (a) {
        items.push({ id: a.id, quantity: 1 });
      });

      var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      var body = { items: items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map(function (s) {
          return s.id;
        });
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }

      if (btn) {
        btn.disabled = true;
        btn.classList.add('is-loading');
      }

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (res) {
          if (res.status) throw new Error(res.description || res.message || 'Add to cart failed');
          /* Before anything is shown or anywhere is navigated to: the codes are part of
             the price this page promised, so they belong on the cart before the customer
             sees it. Awaited, or the redirect below races the call that applies them. */
          return applyOfferCodes().then(function () {
            return res;
          });
        })
        .then(function (res) {
          if (btn) {
            btn.classList.add('is-done');
            if (el.addLabel) el.addLabel.textContent = 'Added';
            if (el.addAmt) el.addAmt.textContent = '';
          }
          buzz([12, 40, 24]);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pdp2-buy-dock', productVariantId: c.id, cartData: res });
          }
          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(res);
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          } else {
            /* No drawer and no notification on this page — this theme's cart
               type is `notification` and the header is header-home, which
               renders its count server-side and nothing else. So nothing
               visible changed after an add. Update the count in place, and
               reload if there is nothing to update. */
            refreshCartCount();
          }
          /* Straight to the cart, when the template asks for it. The cart is where the
             delivery date, the threshold and the subscribe route live, so for a product
             people buy one of, landing there beats staying on a page whose job is done.
             Left off by default: a page people buy several things from is better served
             by "Add another". */
          if (C.redirectToCart) {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          setTimeout(closeSheet, 320);
          setTimeout(function () {
            if (!btn) return;
            btn.classList.remove('is-done');
            /* "Add another", not back to "Add to bag". The bag is no longer empty, so
               the original label understates what pressing it does and reads as though
               the first press had not worked. */
            addedOnce = true;
            if (el.addLabel) el.addLabel.textContent = 'Add another · ';
            renderCommit();
          }, 2600);
        })
        .catch(function (err) {
          console.error('pdp2:', err);
          if (el.micro) el.micro.textContent = String(err.message || err);
          if (!cart) window.location = (window.routes && window.routes.cart_url) || '/cart';
        })
        .finally(function () {
          if (btn) {
            btn.disabled = false;
            btn.classList.remove('is-loading');
          }
        });
    }

    /* Selectors cover header-home's own count and Dawn's bubble, so this works
       whichever header a template happens to use. */
    var COUNT_SELECTORS = [
      '.header-home__cart-count',
      '#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]',
      '.cart-count-bubble span[aria-hidden="true"]'
    ];

    function refreshCartCount() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (data) {
          if (!data) throw new Error('no cart');
          var n = data.item_count;
          var updated = 0;
          COUNT_SELECTORS.forEach(function (sel) {
            document.querySelectorAll(sel).forEach(function (node) {
              node.textContent = n;
              updated++;
            });
          });

          /* The badge is only rendered when the cart wasn't empty, so on a first
             add there is nothing to update — create it. */
          if (!updated && n > 0) {
            var link = document.querySelector('.header-home__cart');
            if (link) {
              var span = document.createElement('span');
              span.className = 'header-home__cart-count';
              span.textContent = n;
              link.appendChild(span);
              updated++;
            }
          }

          /* Unknown header: a reload is blunt but it is never wrong. */
          if (!updated) window.location.reload();
        })
        .catch(function () {
          window.location.reload();
        });
    }

    if (el.add) el.add.addEventListener('click', addToCart);

    /* ----------------------------------------------------------------- boot
       Opening tier, in order of preference:
         1. the size group + unit count configured on the section
         2. the variant flagged custom.is_best_value
         3. the cheapest rung
       `is_best_value` also drives the "Best per unit" badge, so it is only a
       fallback here — the badge and the opening selection are different ideas
       and a merchant must be able to set them independently. */
    if (useSize && C.defaultSizeGroup && sizes.indexOf(C.defaultSizeGroup) > -1) {
      S.size = C.defaultSizeGroup;
    }
    keepTier(null);

    var wantCount = C.defaultCount ? Number(C.defaultCount) : 0;
    var opening = null;
    if (wantCount > 0) {
      opening = ladder().filter(function (v) {
        return v.count === wantCount;
      })[0];
    }
    if (!opening) {
      opening = ladder().filter(function (v) {
        return v.best;
      })[0];
    }
    if (opening) S.tierId = opening.id;

    lastTotal = total();
    render();

    /* ---- verify the discount, once, after first render ----
       This is the only network call before submit, and it is deliberate: it
       replaces a claim with a fact. It never blocks the first paint, and it only
       runs when there is something to check and member pricing isn't already on.
       Interaction stays entirely local. */
    if (expectedCode && !C.showMember) {
      verifyMemberCode(expectedCode);
    }

    function verifyMemberCode(code) {
      var want = code.toLowerCase();
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (cart) {
          if (!cart) return;
          var hit = (cart.discount_codes || []).some(function (d) {
            return String(d.code || '').toLowerCase() === want && d.applicable !== false;
          });
          if (!hit) return;
          C.showMember = true;
          render();
        })
        .catch(function () {
          /* Unreachable cart: leave it locked. Failing closed shows one fewer
             price; failing open advertises a discount nobody holds. */
        });
    }
  }
})();
